import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import type { WorkOrderItem, SerializedItem, AssignSerialInput } from '../../types';

interface AssignSerialModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: AssignSerialInput) => Promise<void>;
  workOrderItem: WorkOrderItem | null;
  availableSerials: SerializedItem[];
  isLoading?: boolean;
}

export function AssignSerialModal({
  open,
  onClose,
  onSubmit,
  workOrderItem,
  availableSerials,
  isLoading,
}: AssignSerialModalProps) {
  const [search, setSearch] = useState('');
  const [selectedSerialId, setSelectedSerialId] = useState<string | null>(null);

  // Filter serials matching the SKU and search term
  const filteredSerials = useMemo(() => {
    if (!workOrderItem) return [];

    return availableSerials
      .filter((s) => s.mongo_sku_id === workOrderItem.mongo_sku_id)
      .filter((s) => s.item_status === 'in_stock')
      .filter((s) => {
        if (!search) return true;
        const searchLower = search.toLowerCase();
        return (
          s.serial_number.toLowerCase().includes(searchLower) ||
          s.mac_address?.toLowerCase().includes(searchLower) ||
          s.dev_id?.toLowerCase().includes(searchLower)
        );
      });
  }, [availableSerials, workOrderItem, search]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSerialId || !workOrderItem) return;

    try {
      await onSubmit({
        item_id: workOrderItem.id,
        serialized_item_id: selectedSerialId,
      });
      toast.success('Serial number assigned successfully');
      // Reset and close
      setSelectedSerialId(null);
      setSearch('');
      onClose();
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Unknown error occurred';
      toast.error(`Failed to assign serial number: ${message}`);
      console.error('Error assigning serial:', error);
      // Keep form open for retry
    }
  };

  const handleClose = () => {
    setSelectedSerialId(null);
    setSearch('');
    onClose();
  };

  const remaining = workOrderItem
    ? workOrderItem.quantity_ordered - (workOrderItem.quantity_allocated || 0)
    : 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Assign Serial Number</DialogTitle>
          <DialogDescription>
            {workOrderItem && (
              <>
                Select a serial number to assign to <span className="font-medium">{workOrderItem.sku_code}</span>.
                <span className="ml-2 text-muted-foreground">({remaining} remaining to allocate)</span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0 space-y-4">
          <div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by serial, MAC, or device ID..."
              className="h-8"
            />
          </div>

          <div className="flex-1 border rounded-lg overflow-auto min-h-0">
            {filteredSerials.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No available serials found for this SKU.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]"></TableHead>
                    <TableHead>Serial Number</TableHead>
                    <TableHead>MAC Address</TableHead>
                    <TableHead>Device ID</TableHead>
                    <TableHead>Location</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSerials.map((serial) => (
                    <TableRow
                      key={serial.id}
                      className={`cursor-pointer hover:bg-muted/50 ${selectedSerialId === serial.id ? 'bg-muted' : ''}`}
                      onClick={() => setSelectedSerialId(serial.id)}
                    >
                      <TableCell>
                        <input
                          type="radio"
                          name="serial-selection"
                          checked={selectedSerialId === serial.id}
                          onChange={() => setSelectedSerialId(serial.id)}
                          className="h-4 w-4"
                        />
                      </TableCell>
                      <TableCell className="font-mono text-sm font-medium">
                        {serial.serial_number}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {serial.mac_address || '-'}
                      </TableCell>
                      <TableCell className="font-mono text-sm text-muted-foreground">
                        {serial.dev_id || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {serial.full_location_code || `${serial.zone_code}-${serial.bin_code}`}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !selectedSerialId}
            >
              {isLoading ? 'Assigning...' : 'Assign Serial'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
